import { AppSelector } from 'app/store'
import getDataSelector from 'core/utils/getDataSelector'
import { createSharedSelector } from 'core/utils/selectorHelpers'
import DataKeys from 'k8s/DataKeys'
import { kaapiMachineDeploymentsSelector } from '../machine-deployment/selectors'
import {
  kaapiNodesSelector,
  kaapiNodesSelectorForAllClusters,
} from '../workload-clusters/nodes/selectors'
import { kaapiConfigMapsSelector } from '../config-maps/selectors'
import {
  formatGPUSizesToString,
  getGPUProfiles,
} from 'k8s/components/cluster/deployment/form-components/helpers'

export enum GPUPartitioningStrategy {
  Passthrough = 'Passthrough',
  MIG = 'MIG',
  Timeslice = 'Time-sliced',
}

// Common function to process kaapi machines data for cluster specific nodes or all clusters
const processMachinesData = (kaapiMachines, machineDeployments, kaapiNodes, kaapiConfigMaps) => {
  return kaapiMachines.map((kaapiMachine) => {
    // Find the openstack machine template for the machine deployment
    const machineDeployment = machineDeployments?.find((md) => {
      return (
        md?.metadata?.name === kaapiMachine?.metadata?.labels['cluster.x-k8s.io/deployment-name']
      )
    })

    // Get the physical node for the machine
    const physicalNode = kaapiMachine?.status?.nodeRef?.name

    // Find the k8s node for the machine
    const k8sNode = kaapiNodes?.find((node) => {
      return (
        node?.metadata?.name === physicalNode ||
        node?.metadata?.name === kaapiMachine?.metadata?.name
      )
    })

    const k8sNodeStatus = k8sNode?.status?.conditions
      ? k8sNode?.status?.conditions?.find((c) => c.status === 'True') ?? { type: 'NotReady' }
      : 'Unknown'
    const isBYOMachine = kaapiMachine?.spec?.infrastructureRef?.kind === 'ByoMachine'

    // GPU Details
    const gpuModel = k8sNode?.metadata?.labels?.['nvidia.com/gpu.product'] || ''
    const migConfig = k8sNode?.metadata?.labels?.['nvidia.com/mig.config'] || ''
    const gpuMemory = k8sNode?.metadata?.labels?.['nvidia.com/gpu.memory'] || ''

    // Find the matching MIG profile from config maps
    let gpuMigProfile = ''
    if (gpuModel && migConfig) {
      const { gpuSizes } = getGPUProfiles(kaapiConfigMaps, gpuModel, gpuMemory)
      gpuMigProfile = gpuSizes?.[migConfig] ? formatGPUSizesToString(gpuSizes?.[migConfig]) : ''
    }
    const gpuEnabled = gpuModel ? true : false
    const gpuPartitioningStrategy = gpuEnabled
      ? migConfig !== 'all-disabled' &&
        k8sNode?.metadata?.labels?.['nvidia.com/gpu.deploy.mig-manager'] === 'true'
        ? GPUPartitioningStrategy.MIG
        : k8sNode?.metadata?.labels?.['nvidia.com/gpu.sharing-strategy'] === 'time-slicing'
        ? GPUPartitioningStrategy.Timeslice
        : GPUPartitioningStrategy.Passthrough
      : ''
    const gpuMachine = k8sNode?.metadata?.labels?.['nvidia.com/gpu.machine'] || ''
    const gpuReplicas = k8sNode?.metadata?.labels?.['nvidia.com/gpu.replicas'] || ''
    const gpuCount = k8sNode?.metadata?.labels?.['nvidia.com/gpu.count'] || ''
    return {
      ...kaapiMachine,
      name: kaapiMachine?.metadata?.name,
      clusterName: kaapiMachine?.spec?.clusterName,
      phase: kaapiMachine?.status?.phase,
      k8sNodeStatus,
      k8sNodeStatusType: k8sNodeStatus?.type,
      isBYOMachine,
      unschedulable: k8sNode?.spec?.unschedulable,
      uid: kaapiMachine?.metadata?.uid,
      machineDeployment,
      cpuUsage: { current: k8sNode?.usedCpu?.toFixed(2), total: k8sNode?.maxCpu?.toFixed(2) },
      memoryUsage: {
        current: k8sNode?.usedMemory?.toFixed(2),
        total: k8sNode?.maxMemory?.toFixed(2),
      },
      storageUsage: { current: 5.5, total: 40 },
      physicalNode,
      gpu: {
        enabled: gpuEnabled,
        model: gpuModel,
        partitioningStrategy: gpuPartitioningStrategy,
        details: gpuMigProfile ? `${gpuMachine} | ${gpuMigProfile}` : gpuMachine,
        memory: gpuMemory,
        replicas: gpuReplicas,
        count: gpuCount,
        migConfig: migConfig,
      },
    }
  })
}

export const kaapiMachinesSelector: AppSelector<any[]> = createSharedSelector(
  getDataSelector<DataKeys.KaapiMachines>(DataKeys.KaapiMachines),
  kaapiMachineDeploymentsSelector,
  kaapiNodesSelector,
  kaapiConfigMapsSelector,
  (kaapiMachines, machineDeployments, kaapiNodes, kaapiConfigMaps): any[] =>
    processMachinesData(kaapiMachines, machineDeployments, kaapiNodes, kaapiConfigMaps),
)

export const kaapiMachinesSelectorAllClusters: AppSelector<any[]> = createSharedSelector(
  getDataSelector<DataKeys.KaapiMachines>(DataKeys.KaapiMachines),
  kaapiMachineDeploymentsSelector,
  kaapiNodesSelectorForAllClusters,
  kaapiConfigMapsSelector,
  (kaapiMachines, machineDeployments, kaapiNodes, kaapiConfigMaps): any[] =>
    processMachinesData(kaapiMachines, machineDeployments, kaapiNodes, kaapiConfigMaps),
)

export const kaapiMachinesForClusterSelector = ({ clusterName }): AppSelector<any[]> =>
  createSharedSelector(kaapiMachinesSelector, (kaapiMachinesSelector): any[] => {
    return kaapiMachinesSelector.filter((md) => md.spec.clusterName === clusterName)
  })
